import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { withFormik } from 'formik';
import * as yup from 'yup';
import isEmpty from 'lodash.isempty';

import {
  Modal,
  Heading,
  Label,
  Input,
  Select,
  Button,
  ButtonGroup,
  ErrorMsg,
} from 'next/components/ui';
import { JOB_DROPDOWN_OPTIONS } from 'constants/inviteModal';
import UserPermissionsSelectInput from 'components/Team/UserPermissionsSelectInput';
import { PUBLISHER_OPTION } from 'constants/accountManagement/permissions';

const Form = styled.form`
  ${Heading} {
    margin-bottom: 20px;
  }

  ${ButtonGroup} {
    margin-top: 20px;
  }

  ${Label} {
    color: var(--heading-primary);
  }
`;

export const InviteTeammate = ({
  discoverable,
  enableEditorRole,
  errors = {},
  hasSSO,
  resetForm,
  setTouched,
  setValues,
  touched = {},
  values = {},
  visible,
  onClose,
  onSubmit,
}) => {
  const [inFlight, setInFlight] = useState(false);

  useEffect(() => {
    if (visible) {
      resetForm();
      setInFlight(false);
    }
  }, [visible]);

  const handleSubmit = event => {
    event.preventDefault();

    const { firstName, lastName, email, job, role } = values;
    const invitation = {
      name: `${firstName} ${lastName}`.trim(),
      email,
      job,
      role: hasSSO && discoverable ? PUBLISHER_OPTION.value : role,
    };

    // remove empty optional fields
    const sanitized = Object.fromEntries(
      Object.entries(invitation).filter(([, field]) => field)
    );

    setInFlight(true);
    onSubmit(sanitized);
  };

  const disabled = inFlight || isEmpty(touched) || !isEmpty(errors);

  return (
    <Modal onClose={onClose} size="s" visible={visible}>
      <Form onSubmit={handleSubmit}>
        <Heading>Invite a teammate</Heading>
        {!hasSSO && (
          <>
            <Label>
              First Name
              <Input
                type="text"
                name="firstName"
                placeholder="Enter first name"
                error={!!errors.firstName && touched.firstName}
                onChange={({ target: { value } }) =>
                  setValues({ ...values, firstName: value })
                }
                onBlur={() => setTouched({ ...touched, firstName: true })}
                value={values.firstName}
              />
              {errors.firstName && touched.firstName && (
                <ErrorMsg>{errors.firstName}</ErrorMsg>
              )}
            </Label>

            <Label>
              Last Name
              <Input
                type="text"
                name="lastName"
                placeholder="Enter last name"
                error={!!errors.lastName && touched.lastName}
                onChange={({ target: { value } }) =>
                  setValues({ ...values, lastName: value })
                }
                onBlur={() => setTouched({ ...touched, lastName: true })}
              />
              {errors.lastName && touched.lastName && (
                <ErrorMsg>{errors.lastName}</ErrorMsg>
              )}
            </Label>
          </>
        )}
        <Label>
          Email
          <Input
            type="text"
            name="email"
            placeholder="Enter email"
            error={!!errors.email && touched.email}
            onChange={({ target: { value } }) =>
              setValues({ ...values, email: value })
            }
            onBlur={() => setTouched({ ...touched, email: true })}
            value={values.email}
          />
          {errors.email && touched.email && <ErrorMsg>{errors.email}</ErrorMsg>}
        </Label>
        <Label>
          Job
          <Select
            name="job"
            options={JOB_DROPDOWN_OPTIONS}
            placeholder="Select a job"
            portal
            error={!!errors.job && !!touched.job}
            onChange={({ value }) => setValues({ ...values, job: value })}
            onBlur={() => setTouched({ ...touched, job: true })}
          />
          {errors.job && touched.job && <ErrorMsg>{errors.job}</ErrorMsg>}
        </Label>

        {!discoverable && (
          <Label>
            Permissions
            <UserPermissionsSelectInput
              name="role"
              onSelectPermission={({ role }) => {
                setValues({ ...values, role });
              }}
              hasEditorPermissionsRole={enableEditorRole}
              placeholder="Select a role"
              error={!!errors.role && !!touched.role}
              onBlur={() => setTouched({ ...touched, role: true })}
            />
            {errors.role && touched.role && <ErrorMsg>{errors.role}</ErrorMsg>}
          </Label>
        )}
        <ButtonGroup right>
          <Button kind="primary" type="submit" disabled={disabled}>
            Invite
          </Button>
          <Button kind="secondary" onClick={onClose}>
            Cancel
          </Button>
        </ButtonGroup>
      </Form>
    </Modal>
  );
};

const formShape = {
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  email: PropTypes.string,
  job: PropTypes.string,
  role: PropTypes.string,
};

InviteTeammate.propTypes = {
  discoverable: PropTypes.bool,
  hasEditor: PropTypes.bool,
  hasSSO: PropTypes.bool,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  visible: PropTypes.bool,

  // formik HOC props
  errors: PropTypes.shape(formShape),
  resetForm: PropTypes.func,
  setTouched: PropTypes.func,
  setValues: PropTypes.func,
  touched: PropTypes.shape({
    firstName: PropTypes.bool,
    lastName: PropTypes.bool,
    email: PropTypes.bool,
    job: PropTypes.bool,
    role: PropTypes.bool,
  }),
  values: PropTypes.shape(formShape),
};

const enhanceForm = withFormik({
  enableReinitialize: true,
  // To initialize form values
  mapPropsToValues: () => ({
    firstName: '',
    lastName: '',
    email: '',
    job: '',
    role: '',
  }),
  validationSchema: ({ discoverable, hasSSO }) => {
    const schema = {
      email: yup
        .string()
        .email('Must be a valid address.')
        .required('Email is required.'),
      job: yup.string().required('Job is required.'),
    };

    if (!hasSSO) {
      schema.firstName = yup.string().required('First name is required.');
      schema.lastName = yup.string().required('Last name is required.');
    }

    if (!hasSSO || (hasSSO && !discoverable)) {
      schema.role = yup.string().required('Role is required.');
    }

    return yup.object().shape(schema);
  },
});

export default enhanceForm(InviteTeammate);
